import { useEffect, useState } from 'react';
import { Box } from '@gemini/core';
import { Button, FeedbackMessage } from '@gemini/ui';
import { ArrowRightIcon } from '@gemini/icons';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from '@aviv-unified-frontend/runtime/i18n';

import { isFrenchPortal as getIsFrenchPortal } from '@/frontend/src/helpers/portal';
import { useBreakpoint } from '@/frontend/src/hooks/useBreakpoint';
import {
    useAppContext,
    useAppDispatch,
} from '@/frontend/src/contexts/appContext';
import { EActionType, EActiveView } from '@/frontend/src/types/context';
import type {
    IClosestLocationResult,
    IGeocodingParams,
} from '@/frontend/src/types/geo';
import { fetchClosestLocation, getCountries } from '@/frontend/src/helpers/geo';
import ClosestLocationSearchField from '@/frontend/src/components/ClosestLocationSearchField';
import { NAME_SPACE } from '@/frontend/src/hooks/useLoadTranslations';

const ErrorFields = ['street', 'houseNumber', 'postalCode', 'city'];

const ClosestLocationSearch = () => {
    const { geo, mobileBreakpoint, portal } = useAppContext();
    const { isMobile } = useBreakpoint(mobileBreakpoint);
    const { t } = useTranslation(NAME_SPACE);

    const {
        handleSubmit,
        clearErrors,
        formState: { errors },
        control,
    } = useFormContext();

    const dispatch = useAppDispatch();
    const [isNoResultsDisplayed, setIsNoResultsDisplayed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { street, houseNumber, postalCode, city } = useWatch({ control });
    const isFrenchPortal = getIsFrenchPortal(portal);

    const onSubmit = async () => {
        const params = {
            address: street,
            number: houseNumber ?? '',
            postal_code: postalCode,
            city,
            country: getCountries(geo, portal)[0],
        };

        setIsNoResultsDisplayed(false);
        setIsLoading(true);

        const result: IClosestLocationResult | undefined =
            await fetchClosestLocation(params as IGeocodingParams);

        setIsLoading(false);

        if (result) {
            setIsNoResultsDisplayed(false);
            dispatch({
                type: EActionType.SetClosestLocationResult,
                payload: result,
            });
            dispatch({
                type: EActionType.SetActiveView,
                payload: EActiveView.ClosestLocationResult,
            });
        } else {
            setIsNoResultsDisplayed(true);
        }
    };

    const setFocus = (name: string) => {
        const field = document.getElementsByName(
            name,
        )[0] as HTMLInputElement | null;

        field?.focus();
    };

    const getGridTemplateAreas = () => {
        if (isFrenchPortal) {
            return isMobile
                ? '"houseNumber street" "postalCode postalCode" "city city" "noResult noResult" "ctabutton ctabutton"'
                : '"houseNumber street ctabutton" "postalCode postalCode ." "city city ." "noResult noResult ."';
        }

        return isMobile
            ? '"street houseNumber" "postalCode postalCode" "city city" "noResult noResult" "ctabutton ctabutton"'
            : '"street houseNumber ctabutton" "postalCode postalCode ." "city city ." "noResult noResult ."';
    };

    const getGridTemplateColumns = () => {
        if (isFrenchPortal) return isMobile ? '96px 1fr' : '96px 1fr 1fr';

        return isMobile ? '1fr 96px' : '1fr 96px 1fr';
    };

    // Focus on the first field with an error
    useEffect(() => {
        const firstErrorField = ErrorFields.find((field) => errors[field]);

        if (firstErrorField) {
            setIsNoResultsDisplayed(false);
            setFocus(firstErrorField);
        }
    }, [errors]);

    useEffect(() => {
        ErrorFields.forEach((field) => {
            clearErrors(field);
        });
    }, [clearErrors]);

    return (
        <Box
            as="form"
            onSubmit={handleSubmit(onSubmit)}
            display="grid"
            gridTemplateAreas={getGridTemplateAreas()}
            gridTemplateColumns={getGridTemplateColumns()}
            gap="spacing.16"
        >
            {isFrenchPortal && (
                <ClosestLocationSearchField name="houseNumber" />
            )}
            <ClosestLocationSearchField name="street" />
            {!isFrenchPortal && (
                <ClosestLocationSearchField name="houseNumber" />
            )}
            <ClosestLocationSearchField name="postalCode" />
            <ClosestLocationSearchField name="city" />
            <Box
                gridArea="ctabutton"
                display="flex"
                flexDirection="column"
                marginTop="auto"
                marginBottom={errors.street ? 'auto' : ''}
            >
                <Button
                    marginTop="auto"
                    type="submit"
                    icon={ArrowRightIcon}
                    iconPosition="end"
                    isFullWidth
                    isLoading={isLoading}
                >
                    {t('common.startMyEstimationButton.label')}
                </Button>
            </Box>
            {isNoResultsDisplayed && (
                <Box
                    gridArea="noResult"
                    display="flex"
                    flexDirection="column"
                >
                    <FeedbackMessage
                        marginBottom={isMobile ? 'spacing.16' : 'spacing.none'}
                        variant="warning"
                        title={t('common.informationMessage.noResults.title')}
                    >
                        {t('common.informationMessage.noResults.label')}
                    </FeedbackMessage>
                </Box>
            )}
        </Box>
    );
};

export default ClosestLocationSearch;
